/**
 * CRM — Acompanhamento de inscrições em cursos da downline.
 *
 * Lista cursos futuros onde os clientes da downline (recursiva) compraram
 * ou se inscreveram, e detalhe por curso com o estado de cada cliente
 * (Comprou / Inscrito / Ficha / Contrato / Doc Menores / Confirmado) +
 * link público e QR code para os que já têm ficha.
 *
 * Visualização apenas — sem acções de criação.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  CalendarDays, MapPin, Users, ChevronRight, ArrowLeft, Loader2,
  CheckCircle2, Circle, Copy, ExternalLink, Search, Mail, Phone, User,
  ChevronDown, ChevronUp,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import api from "@/lib/api";

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("pt-PT");
  } catch { return s; }
}

function StatusCheck({ value, label, testid }) {
  return (
    <div className="flex items-center gap-1.5 text-xs" data-testid={testid}>
      {value
        ? <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
        : <Circle size={13} className="text-[#D5CDB9] shrink-0" />}
      <span className={value ? "text-emerald-800" : "text-[#978D78]"}>{label}</span>
    </div>
  );
}

function FichaSubCard({ f }) {
  const confirmado = !!f.DataConfirmacao && !!f.FichaPreenchida && !!f.ContratoPreenchido;
  return (
    <div className="p-3 bg-[#FAF8F2] border border-[#E6E1D6] rounded-md" data-testid={`ficha-${f.ID_FichaInscricao}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <User size={12} className="text-[#978D78] shrink-0" />
            <span className="text-sm font-medium text-[#0F1829]" data-testid={`ficha-nome-${f.ID_FichaInscricao}`}>
              {f.NomeCompleto || "(sem nome)"}
            </span>
            <span className="text-[10px] font-mono text-[#978D78]">#{f.ID_FichaInscricao}</span>
          </div>
          {(f.Email || f.Telefone) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-[#978D78]">
              {f.Email && <span className="inline-flex items-center gap-1"><Mail size={10} /> {f.Email}</span>}
              {f.Telefone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {f.Telefone}</span>}
            </div>
          )}
        </div>
        {confirmado && (
          <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-emerald-100 text-emerald-900 shrink-0"
            data-testid={`ficha-confirmado-${f.ID_FichaInscricao}`}>
            CONFIRMADO
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mt-2">
        <StatusCheck value={f.FichaPreenchida} label="Ficha" testid={`ficha-s-ficha-${f.ID_FichaInscricao}`} />
        <StatusCheck value={f.ContratoPreenchido} label="Contrato" testid={`ficha-s-contrato-${f.ID_FichaInscricao}`} />
        <StatusCheck value={f.MenorPreenchido} label="Doc. menor" testid={`ficha-s-menor-${f.ID_FichaInscricao}`} />
        <StatusCheck value={confirmado} label="Confirmado" testid={`ficha-s-confirmado-${f.ID_FichaInscricao}`} />
      </div>
    </div>
  );
}

// ── LISTA DE CURSOS ─────────────────────────────────────────────────────
export function CRMCursosInscricoesListPage() {
  const [data, setData] = useState({ cursos: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/crm/cursos/inscricoes`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) toast.error(e.response?.data?.detail || "Erro ao carregar cursos");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = data.cursos.filter((c) =>
    !q || (c.NomeCurso || "").toLowerCase().includes(q.toLowerCase()) ||
    (c.Local || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="crm-cursos-inscricoes-list">
      <h1 className="text-2xl font-bold text-[#0F1829] mb-1">Inscrições da minha rede em cursos</h1>
      <p className="text-sm text-[#978D78] mb-6">
        Cursos futuros onde clientes da sua rede (downline recursiva) compraram ou se inscreveram.
        Clique num curso para ver o detalhe.
      </p>

      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#978D78]" />
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filtrar por nome ou local…"
          data-testid="cursos-search"
          className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-[#E6E1D6] rounded-md text-[#0F1829]" />
      </div>

      {loading ? (
        <div className="p-12 text-center text-[#978D78]">
          <Loader2 size={24} className="animate-spin mx-auto mb-2" />
          A carregar cursos…
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center bg-[#FAF8F2] border border-[#E6E1D6] rounded-md">
          <p className="text-[#0F1829] font-medium">Sem cursos com inscrições da sua rede.</p>
          <p className="text-xs text-[#978D78] mt-1">
            Quando algum cliente da sua downline comprar um curso futuro, aparecerá aqui.
          </p>
        </div>
      ) : (
        <div className="grid gap-3" data-testid="cursos-grid">
          {filtered.map((c) => (
            <Link key={c.ID_Curso}
              to={`/cursos/inscricoes/${c.ID_Curso}`}
              data-testid={`curso-card-${c.ID_Curso}`}
              className="block p-4 bg-white border border-[#E6E1D6] rounded-md hover:shadow-md hover:border-[#0F1829] transition group">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-[#0F1829] group-hover:text-[#001f6e]">
                    {c.NomeCurso}
                    {c.Edicao && <span className="ml-2 text-xs font-normal text-[#978D78]">ed. {c.Edicao}</span>}
                  </h3>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-[#978D78]">
                    <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {fmtDate(c.DataInicio)}{c.DataFim && c.DataFim !== c.DataInicio ? ` → ${fmtDate(c.DataFim)}` : ""}</span>
                    {c.Local && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {c.Local}</span>}
                    {c.Horario && <span>{c.Horario}</span>}
                  </div>
                </div>
                <ChevronRight size={18} className="text-[#978D78] group-hover:text-[#0F1829] shrink-0 mt-1" />
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="px-2 py-0.5 text-[10px] font-mono rounded-full bg-[#FAF8F2] text-[#0F1829]" data-testid={`kpi-compras-${c.ID_Curso}`}>
                  {c.NCompras} compra{c.NCompras !== 1 ? "s" : ""}
                </span>
                <span className="px-2 py-0.5 text-[10px] font-mono rounded-full bg-blue-50 text-blue-900" data-testid={`kpi-inscritos-${c.ID_Curso}`}>
                  {c.NInscritos} inscrito{c.NInscritos !== 1 ? "s" : ""}
                </span>
                <span className="px-2 py-0.5 text-[10px] font-mono rounded-full bg-emerald-50 text-emerald-900" data-testid={`kpi-confirmados-${c.ID_Curso}`}>
                  {c.NConfirmados} confirmado{c.NConfirmados !== 1 ? "s" : ""}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ── DETALHE DE 1 CURSO ──────────────────────────────────────────────────
function ClienteCard({ c, curso }) {
  const [showQR, setShowQR] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const linkToken = c.Token || c.FichaLinkToken;
  const publicUrl = linkToken
    ? `${window.location.origin}/cursos/inscricao/${linkToken}`
    : null;
  // Só mostrar QR se: existe CodigoBarras E todos os docs assinados
  const docsCompletos = !!c.FichaPreenchida && !!c.ContratoPreenchido;
  const showQrButton = !!c.CodigoBarras && docsCompletos;
  const comprou = !!c.ID_Encomenda;
  const inscrito = !!c.ID_FichaInscricao;
  const confirmado = !!c.DataConfirmacao && !!c.FichaPreenchida && !!c.ContratoPreenchido;

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copiado");
    } catch {
      toast.error("Falhou — selecione e copie manualmente");
    }
  };

  return (
    <div className="p-4 bg-white border border-[#E6E1D6] rounded-md" data-testid={`cliente-${c.ID_Cliente}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-[#0F1829] flex items-center gap-1.5 flex-wrap" data-testid={`nome-${c.ID_Cliente}`}>
            <User size={14} className="text-[#978D78] shrink-0" />
            {c.NomeCompleto}
            <span className="text-[10px] font-mono text-[#978D78]">N{c.Nivel}</span>
            {c.ID_TipoCliente && <span className="text-[10px] font-mono text-[#978D78]">{c.ID_TipoCliente}</span>}
          </h4>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs">
            <a href={c.Email ? `mailto:${c.Email}` : undefined}
              className={`inline-flex items-center gap-1.5 ${c.Email ? "text-[#0F1829] hover:text-[#001f6e] hover:underline" : "text-[#978D78]"}`}
              data-testid={`email-${c.ID_Cliente}`}>
              <Mail size={12} /> {c.Email || "sem email"}
            </a>
            <a href={c.Telefone ? `tel:${c.Telefone}` : undefined}
              className={`inline-flex items-center gap-1.5 ${c.Telefone ? "text-[#0F1829] hover:text-[#001f6e] hover:underline" : "text-[#978D78]"}`}
              data-testid={`telefone-${c.ID_Cliente}`}>
              <Phone size={12} /> {c.Telefone || "sem telefone"}
            </a>
          </div>
          {c.Sponsor && c.Nivel > 1 && (
            <div className="text-[10px] text-[#978D78] mt-1">via {c.Sponsor}</div>
          )}
        </div>
        {confirmado && (
          <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-900 shrink-0">
            CONFIRMADO
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 mt-3 pt-3 border-t border-[#E6E1D6]">
        <StatusCheck value={comprou} label={comprou ? `Comprou${c.Quantidade > 1 ? ` (qty ${c.Quantidade})` : ""}` : "Sem compra"} testid={`s-comprou-${c.ID_Cliente}`} />
        <StatusCheck value={inscrito} label="Inscrito" testid={`s-inscrito-${c.ID_Cliente}`} />
        <StatusCheck value={c.FichaPreenchida} label="Ficha preenchida" testid={`s-ficha-${c.ID_Cliente}`} />
        <StatusCheck value={c.ContratoPreenchido} label="Contrato preenchido" testid={`s-contrato-${c.ID_Cliente}`} />
        <StatusCheck value={c.MenorPreenchido} label="Doc. menores" testid={`s-menor-${c.ID_Cliente}`} />
        <StatusCheck value={confirmado} label="Confirmado" testid={`s-confirmado-${c.ID_Cliente}`} />
      </div>

      {comprou && (c.ID_Encomenda || c.Quantidade) && (
        <div className="mt-3 pt-3 border-t border-[#E6E1D6] flex flex-wrap items-center gap-2 text-[11px]">
          {c.ID_Encomenda && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FAF8F2] text-[#0F1829] font-mono"
              data-testid={`encomenda-${c.ID_Cliente}`}>
              Encomenda #{c.ID_Encomenda}
            </span>
          )}
          {c.Quantidade ? (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono ${
              c.FichasCompletas >= c.Quantidade
                ? "bg-emerald-50 text-emerald-900"
                : c.FichasCompletas > 0
                  ? "bg-amber-50 text-amber-900"
                  : "bg-[#FAF8F2] text-[#978D78]"
            }`}
              data-testid={`fichas-completas-${c.ID_Cliente}`}
              title="Inscrições com formulário totalmente preenchido / total da encomenda">
              {c.FichasCompletas ?? 0}/{c.Quantidade} inscrição{c.Quantidade !== 1 ? "(ões)" : ""} completa{c.Quantidade !== 1 ? "s" : ""}
            </span>
          ) : null}
          {(c.Fichas?.length || 0) > 0 && (
            <button onClick={() => setExpanded((v) => !v)}
              data-testid={`toggle-fichas-${c.ID_Cliente}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-[#E6E1D6] text-[#0F1829] hover:bg-[#FAF8F2] transition">
              {expanded
                ? <><ChevronUp size={12} /> Esconder inscrições</>
                : <><ChevronDown size={12} /> Ver {c.Fichas.length} inscriç{c.Fichas.length === 1 ? "ão" : "ões"}</>}
            </button>
          )}
        </div>
      )}

      {expanded && (c.Fichas?.length || 0) > 0 && (
        <div className="mt-3 space-y-2" data-testid={`fichas-list-${c.ID_Cliente}`}>
          {c.Fichas.map((f) => <FichaSubCard key={f.ID_FichaInscricao} f={f} />)}
        </div>
      )}

      {publicUrl && (
        <div className="mt-3 pt-3 border-t border-[#E6E1D6]">
          <div className="flex items-center gap-2">
            <input type="text" value={publicUrl} readOnly
              onFocus={(e) => e.target.select()}
              data-testid={`link-url-${c.ID_Cliente}`}
              className="flex-1 px-2 py-1 text-[10px] font-mono bg-[#FAF8F2] border border-[#E6E1D6] rounded text-[#0F1829]" />
            <button onClick={copyLink}
              data-testid={`copy-link-${c.ID_Cliente}`}
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded bg-[#0F1829] text-white hover:bg-[#001f6e] transition">
              <Copy size={11} /> Copiar
            </button>
            <a href={publicUrl} target="_blank" rel="noopener noreferrer"
              data-testid={`open-link-${c.ID_Cliente}`}
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] rounded border border-[#E6E1D6] hover:bg-[#FAF8F2] transition text-[#0F1829]">
              <ExternalLink size={11} /> Abrir
            </a>
          </div>
          {showQrButton && (
            <div className="mt-2">
              <button onClick={() => setShowQR((v) => !v)}
                data-testid={`toggle-qr-${c.ID_Cliente}`}
                className="text-[10px] text-[#0F1829] underline hover:no-underline">
                {showQR ? "Esconder QR de check-in" : `Ver QR de check-in (${c.CodigoBarras})`}
              </button>
              {showQR && (
                <div className="mt-2 p-3 bg-white border border-[#E6E1D6] rounded inline-block" data-testid={`qr-svg-${c.ID_Cliente}`}>
                  <QRCodeSVG value={c.CodigoBarras} size={140} level="M"
                    fgColor="#0F1829" bgColor="#ffffff" />
                  <div className="text-center text-[9px] font-mono text-[#978D78] mt-1">{c.CodigoBarras}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function CRMCursoInscricoesDetailPage() {
  const { cursoId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all|sem_ficha|sem_confirmacao

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/crm/cursos/inscricoes/${cursoId}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar curso");
      nav("/cursos/inscricoes");
    } finally {
      setLoading(false);
    }
  }, [cursoId, nav]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="p-12 text-center text-[#978D78]" data-testid="loading">
        <Loader2 size={24} className="animate-spin mx-auto mb-2" />A carregar…
      </div>
    );
  }
  if (!data) return null;

  const c = data.curso;
  const matchesQ = (cl) => {
    if (!q) return true;
    const ql = q.toLowerCase();
    return (cl.NomeCompleto || "").toLowerCase().includes(ql) || (cl.Email || "").toLowerCase().includes(ql);
  };
  const matchesFilter = (cl) => {
    if (filter === "sem_ficha") return !cl.ID_FichaInscricao;
    if (filter === "sem_confirmacao") return cl.ID_FichaInscricao && !cl.DataConfirmacao;
    return true;
  };
  const visible = data.clientes.filter((cl) => matchesQ(cl) && matchesFilter(cl));

  const nCompras = data.clientes.filter((cl) => cl.ID_Encomenda).length;
  const nVagas = data.clientes.reduce((s, cl) => s + (cl.Quantidade || 0), 0);
  const nInscritos = data.clientes.filter((cl) => cl.ID_FichaInscricao).length;
  const nConfirmados = data.clientes.filter((cl) => cl.DataConfirmacao && cl.FichaPreenchida && cl.ContratoPreenchido).length;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="crm-curso-detalhe">
      <Link to="/cursos/inscricoes"
        data-testid="back-to-list"
        className="inline-flex items-center gap-1 text-xs text-[#978D78] hover:text-[#0F1829] mb-3">
        <ArrowLeft size={12} /> Voltar à lista
      </Link>
      <h1 className="text-2xl font-bold text-[#0F1829]">{c.NomeCurso}</h1>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-[#978D78]">
        <span className="inline-flex items-center gap-1"><CalendarDays size={13} /> {fmtDate(c.DataInicio)}{c.DataFim && c.DataFim !== c.DataInicio ? ` → ${fmtDate(c.DataFim)}` : ""}</span>
        {c.Local && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {c.Local}</span>}
        {c.Horario && <span>{c.Horario}</span>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
        <div className="p-3 bg-white border border-[#E6E1D6] rounded-md text-center">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#978D78] font-bold">Compras</p>
          <p className="text-2xl font-bold text-[#0F1829] mt-1" data-testid="kpi-compras">{nCompras}</p>
        </div>
        <div className="p-3 bg-white border border-[#E6E1D6] rounded-md text-center">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#978D78] font-bold">Vagas compradas</p>
          <p className="text-2xl font-bold text-[#0F1829] mt-1" data-testid="kpi-vagas">{nVagas}</p>
        </div>
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-center">
          <p className="text-[10px] uppercase tracking-[0.18em] text-blue-900 font-bold">Inscritos</p>
          <p className="text-2xl font-bold text-blue-800 mt-1" data-testid="kpi-inscritos">{nInscritos}</p>
        </div>
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md text-center">
          <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-900 font-bold">Confirmados</p>
          <p className="text-2xl font-bold text-emerald-800 mt-1" data-testid="kpi-confirmados">{nConfirmados}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-6 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#978D78]" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="clientes-search"
            placeholder="Filtrar por nome ou email…"
            className="w-full pl-9 pr-3 py-1.5 text-xs bg-white border border-[#E6E1D6] rounded-md text-[#0F1829]" />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
          data-testid="clientes-filter"
          className="px-2 py-1.5 text-xs bg-white border border-[#E6E1D6] rounded-md text-[#0F1829]">
          <option value="all">Todos ({data.total})</option>
          <option value="sem_ficha">Sem ficha ({data.clientes.filter((cl) => !cl.ID_FichaInscricao).length})</option>
          <option value="sem_confirmacao">Inscritos sem confirmação ({data.clientes.filter((cl) => cl.ID_FichaInscricao && !cl.DataConfirmacao).length})</option>
        </select>
      </div>

      <div className="grid gap-3" data-testid="clientes-list">
        {visible.length === 0 ? (
          <div className="p-8 text-center text-[#978D78] bg-[#FAF8F2] border border-[#E6E1D6] rounded-md">
            <Users size={24} className="mx-auto mb-2 opacity-50" />
            Sem clientes para os filtros seleccionados.
          </div>
        ) : visible.map((cl, idx) => (
          <ClienteCard key={`${cl.ID_Cliente}-${cl.ID_FichaInscricao || cl.ID_LinkInscricao || idx}`} c={cl} curso={c} />
        ))}
      </div>
    </div>
  );
}
